<template>
	<v-list-item
		:class="['document-item', { 'document-item--selected': selected }]"
		data-test="document-file"
		@click="onRowClick"
		@keydown.enter.prevent="onRowClick"
		@keydown.space.prevent="onToggleSelected"
	>
		<template #prepend>
			<!--
				Auswahl: die Zeile selbst öffnet die Vorschau, gewählt wird über die
				Checkbox. Die Auswahl lebt in der aufrufenden Liste (eine Menge von
				IDs) und übersteht Ansichts-, Seiten- und Filterwechsel — deshalb
				steuert die Liste den Zustand und nicht die v-list.
			-->
			<v-checkbox-btn
				v-if="selectable"
				:aria-label="name"
				:model-value="selected"
				data-test="document-file-select"
				density="compact"
				hide-details
				@click.stop
				@update:model-value="emit('update:selected', $event === true)"
			></v-checkbox-btn>

			<v-avatar
				v-if="avatar"
				:color="legacyIcon.color"
				:icon="legacyIcon.icon"
				class="ms-1"
				size="x-small"
				variant="tonal"
			>
			</v-avatar>
			<!--
				Ohne Avatar-Kreis: das Typ-Icon steht für sich. Rot ist nur das PDF —
				die einzige Farbe im Icon-Satz, damit die Liste ruhig bleibt.
			-->
			<v-icon
				v-else
				:color="typeIcon.color"
				:icon="typeIcon.icon"
				class="ms-1 me-1"
				size="small"
			></v-icon>
		</template>

		<v-list-item-title>
			<span
				v-if="nameHtml"
				:title="name"
				class="document-item__name"
				v-html="nameHtml"
			></span>
			<span v-else :title="name" class="document-item__name">{{ name }}</span>
		</v-list-item-title>

		<v-list-item-subtitle class="document-item__meta">
			{{ metaLine }}
		</v-list-item-subtitle>

		<template v-if="link || action || canDelete || preview" #append>
			<v-list-item-action class="d-flex flex-row align-center ga-1">
				<!--
					Das Auge steht nur bei Dateien, die der Vorschau-Dialog auch zeigen
					kann (PDF/Bild). Bei allen anderen bleibt die Spalte leer, damit der
					Download-Knopf in allen Zeilen an derselben Stelle steht.
				-->
				<v-btn
					v-if="canPreview && !xs"
					:aria-label="t('component.documents.containerDialog.openPreview')"
					data-test="document-file-preview"
					icon="far fa-eye"
					size="x-small"
					variant="text"
					@click.stop="previewOpen = true"
				/>
				<span
					v-else-if="preview && !xs"
					aria-hidden="true"
					class="document-item__preview-spacer"
				></span>
				<v-btn
					v-if="link"
					:to="typeof linkTo === 'function' ? linkTo(item) : linkTo"
					icon="fa-solid fa-up-right-from-square"
					size="x-small"
					target="_blank"
					variant="text"
					@click.stop
				/>
				<!--
					Neutral statt Primary: in jeder Zeile ein roter Knopf machte die
					Liste laut, und Rot heißt je nach Mandant „Fehler".
				-->
				<v-btn
					v-if="action"
					:aria-label="t('component.documents.download')"
					:size="xs ? 'small' : 'x-small'"
					class="document-item__download"
					data-test="document-file-download"
					icon="fa fa-download"
					variant="text"
					@click.stop="download"
				/>
				<v-btn
					v-if="canDelete"
					:title="t('common.delete.name')"
					color="error"
					data-test="delete-btn"
					icon="fa fa-trash"
					size="x-small"
					variant="text"
					@click.stop="emit('delete', item)"
				/>
			</v-list-item-action>
		</template>
	</v-list-item>

	<!--
		Erst beim Öffnen im DOM: DialogPreview lädt die Datei in onBeforeMount, das
		darf nicht für jede Zeile der Liste passieren.
	-->
	<DialogPreview
		v-if="previewOpen"
		:id="item.id"
		v-model:dialog="previewOpen"
		:handle-prev-route="false"
		@close="previewOpen = false"
	></DialogPreview>
</template>

<script setup lang="ts">
import DialogPreview from '@/components/modules/Documents/DialogPreview.vue'
import { useDayJs } from '@/composables/dateTime'
import { getFormatBytes } from '@/helpers/FormatValue'
import { useDocumentStore } from '@/stores/modules/document'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import type { RouteLocation } from 'vue-router'

const {
	item,
	action,
	avatar,
	canDelete,
	downloadEndpoint,
	link,
	meta,
	nameHtml,
	preview,
	selectable,
	selected,
	linkTo = (item) => ({
		name: 'document',
		params: { id: item.id },
	}),
} = defineProps<{
	link?: boolean
	dense?: boolean
	action?: boolean
	/** Opt-in: Vorschau-Auge, wo die Datei eine Vorschau hat. */
	preview?: boolean
	/** Nur noch für Bestandsaufrufe: Icon im getönten Kreis statt als Typ-Icon. */
	avatar?: boolean
	/**
	 * Opt-in: blendet den Löschen-Button ein. Standard aus, damit die übrigen
	 * Verwendungen der Liste (Notizen, FAQ, Belegprüfung, E-Mail, verknüpfte
	 * Elemente) unverändert bleiben.
	 */
	canDelete?: boolean
	/** Auswahl-Checkbox; der Zustand liegt in der aufrufenden Liste. */
	selectable?: boolean
	selected?: boolean
	item: Api.Document
	downloadEndpoint?: string
	/** Vorgerenderter Name mit `<mark>` um die Suchtreffer. */
	nameHtml?: string
	/** Überschreibt die Meta-Zeile, z. B. für die schmale Listenansicht. */
	meta?: string
	linkTo?: RouteLocation | ((item: Api.Document) => RouteLocation)
}>()

const emit = defineEmits<{
	delete: [item: Api.Document]
	'update:selected': [value: boolean]
}>()

const { t, locale } = useI18n()
const { saveFile, showWhat } = useDocumentStore()
const { getLocalDate } = useDayJs()
const { xs } = useDisplay()

const previewOpen = ref(false)

/** Bezeichnung der Datei; ohne eigene Bezeichnung der Originalname. */
const name = computed(
	() => item.file_notes || item.file_original_name || String(item.id)
)

const extension = computed(() =>
	(item.file_original_name || item.file_hash_name || '')
		.split('.')
		.pop()
		?.toLowerCase()
)

const TYPE_ICONS: Record<string, { icon: string; color?: string }> = {
	pdf: { icon: 'fa-regular fa-file-pdf', color: 'error' },
	jpg: { icon: 'fa-regular fa-file-image' },
	jpeg: { icon: 'fa-regular fa-file-image' },
	png: { icon: 'fa-regular fa-file-image' },
	gif: { icon: 'fa-regular fa-file-image' },
	webp: { icon: 'fa-regular fa-file-image' },
	bmp: { icon: 'fa-regular fa-file-image' },
	doc: { icon: 'fa-regular fa-file-word' },
	docx: { icon: 'fa-regular fa-file-word' },
	xls: { icon: 'fa-regular fa-file-excel' },
	xlsx: { icon: 'fa-regular fa-file-excel' },
	csv: { icon: 'fa-regular fa-file-excel' },
}

const typeIcon = computed(
	() => TYPE_ICONS[extension.value ?? ''] ?? { icon: 'fa-regular fa-file' }
)

/** Bestandsdarstellung mit Avatar — die Icons kommen aus den i18n-Dateien. */
const legacyIcon = computed(() => ({
	icon: t(`file.${extension.value ?? 'default'}.icon`),
	color: t(`file.${extension.value ?? 'default'}.color`),
}))

const typeLabel = computed(() => {
	const type = item.document_type
	if (!type) return ''
	return [type.acronym, type.display_label || type.name]
		.filter(Boolean)
		.join(' - ')
})

/** `JA - Jahresabrechnung · PDF · 1,2 MB · 12.03.2026` */
const metaLine = computed(() => {
	if (meta !== undefined) return meta
	return [
		typeLabel.value,
		extension.value?.toUpperCase(),
		item.file_size ? getFormatBytes(item.file_size, 1, locale.value) : '',
		getLocalDate(item.created),
	]
		.filter(Boolean)
		.join(' · ')
})

/**
 * Vorschau nur, wenn der Vorschau-Dialog den Typ auch darstellt — showWhat
 * liefert sonst einen leeren String und der Dialog bliebe leer.
 */
const canPreview = computed(() => {
	if (!preview || !item.file_hash_name) return false
	try {
		return showWhat(item) !== ''
	} catch {
		return false
	}
})

const download = (): void => {
	saveFile(item, downloadEndpoint)
}

/**
 * Klick auf die Zeile: Vorschau, wenn es eine gibt, sonst herunterladen —
 * mobil gibt es keinen Hover, dort ist die Zeile das Hauptziel.
 */
const onRowClick = (): void => {
	if (canPreview.value) previewOpen.value = true
	else if (action) download()
}

const onToggleSelected = (): void => {
	if (selectable) emit('update:selected', !selected)
}
</script>
<style scoped>
.document-item__name {
	display: block;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 0.875rem;
}

.document-item__name :deep(mark) {
	background: rgba(var(--v-theme-primary), 0.18);
	color: inherit;
}

.document-item__meta {
	font-size: 12px;
	font-variant-numeric: tabular-nums;
}

/*
	Vuetify setzt hinter den Prepend-Block einen 32px-Abstandshalter — gedacht
	für Listen mit Avatar. Neben Checkbox und Typ-Icon ist das nur verschenkte
	Breite, die auf schmalen Geräten dem Dateinamen fehlt. `--v-list-prepend-gap`
	ist der dafür vorgesehene Regler und gilt für beide Seiten der Zeile.
*/
.document-item {
	--v-list-prepend-gap: 8px;
}

/* Keine farbige Kante mehr für die Auswahl — nur eine ruhige Tönung. */
.document-item--selected {
	background: rgba(var(--v-theme-primary), 0.06);
}

.document-item__download {
	color: rgba(var(--v-theme-on-surface), 0.6);
}

.document-item__meta {
	opacity: 1;
	color: rgba(var(--v-theme-on-surface), 0.55);
}

/* Hält die Download-Spalte bündig, wenn eine Datei keine Vorschau hat. */
.document-item__preview-spacer {
	display: inline-block;
	width: 28px;
}
</style>
